import { Component, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { SystemCard, SystemField } from './system-form-pages';

/**
 * One collapsible section of a page cut from twin-ci System Settings (mockup system-settings section):
 * its fields, Cancel (back to the saved values) and Save Changes, saved through
 * api/system-settings/{card.group}.
 */
@Component({
    selector: 'app-system-card',
    templateUrl: './system-card.component.html',
    styleUrls: ['./system-card.component.scss']
})
export class SystemCardComponent implements OnInit {
    @Input() card: SystemCard;

    values: { [field: string]: any } = {};
    saved: { [field: string]: any } = {};
    errors: { [field: string]: string } = {};
    loaded = false;
    message = '';
    error = '';
    saving = false;
    urls: { [field: string]: string } = {};       // current uploaded files
    files: { [field: string]: File } = {};        // files picked, not saved yet
    private fileInputs: { [field: string]: HTMLInputElement } = {};
    readonly apiSite = environment.apiSite;

    constructor(private http: HttpClient) { }

    ngOnInit() {
        this.loadOptions();
        this.load();
    }

    private load() {
        this.http.get<any>(environment.api + 'system-settings/' + this.card.group).subscribe(
            res => {
                const values = {};
                this.inputs().forEach(f => values[f.field] = this.fromServer(f, res[f.field]));
                this.saved = values;
                this.values = Object.assign({}, values);
                // Defaults fill what was never saved (Save stays enabled until they are saved).
                this.inputs().filter(f => f.default !== undefined && (values[f.field] === '' || values[f.field] === null))
                    .forEach(f => this.values[f.field] = f.default);
                this.card.fields.forEach(f => this.keepSavedOption(f));
                this.urls = res._urls || {};
                this.loaded = true;
            },
            err => this.error = typeof err === 'string' ? err : 'The settings could not be loaded.'
        );
    }

    /** The fields that hold a value (not links, headings or uploads). */
    private inputs() {
        return this.card.fields.filter(f => f.type !== 'link' && f.type !== 'heading' && f.type !== 'image' && !f.mockOnly);
    }

    pick(f: SystemField, input: HTMLInputElement) {
        this.fileInputs[f.field] = input;
        if (input.files && input.files.length) {
            this.files[f.field] = input.files[0];
        } else {
            delete this.files[f.field];
        }
    }

    private clearFiles() {
        this.files = {};
        Object.keys(this.fileInputs).forEach(k => this.fileInputs[k].value = '');
    }

    /** Selects whose options come from an app asset (e.g. the State list). */
    private loadOptions() {
        this.card.fields.filter(f => f.optionsUrl && !f.options).forEach(f =>
            this.http.get<any[]>(f.optionsUrl).subscribe(list => {
                f.options = list.map(o => ({ value: o.id, label: o.name }));
                this.keepSavedOption(f);
            })
        );
    }

    /** A saved value missing from a select's list stays selectable instead of being dropped. */
    private keepSavedOption(f: SystemField) {
        const v = this.saved[f.field];
        if (f.type === 'select' && f.options && v !== null && v !== '' && !f.options.some(o => o.value === v)) {
            f.options = f.options.concat({ value: v, label: String(v) });
        }
    }

    private fromServer(f: SystemField, v: any) {
        if (f.type === 'checkbox') {
            if (f.checkedValue !== undefined) {
                return v === f.checkedValue ? f.checkedValue : '';
            }
            return Number(v) ? 1 : 0;
        }
        if (v === null || v === undefined) {
            return f.type === 'radio' ? null : '';
        }
        if (f.type === 'radio' || (f.type === 'select' && !f.optionsUrl)) {
            if (f.options.some(o => o.value === v)) {
                return v;
            }
            // numeric option lists (twin-ci 0/1 flags) arrive as numbers or strings; keep anything else as saved
            return typeof f.options[0].value === 'number' && isFinite(Number(v)) ? Number(v) : v;
        }
        return f.type === 'int' || f.type === 'decimal' ? Number(v) : String(v);
    }

    minOf(f: SystemField) {
        return f.min === undefined ? 0 : f.min;
    }

    toggle(f: SystemField, checked: boolean) {
        this.values[f.field] = f.checkedValue !== undefined ? (checked ? f.checkedValue : '') : (checked ? 1 : 0);
    }

    isChecked(f: SystemField) {
        return f.checkedValue !== undefined ? this.values[f.field] === f.checkedValue : this.values[f.field] == 1;
    }

    get changed() {
        return Object.keys(this.files).length > 0
            || this.inputs().some(f => String(this.values[f.field]) !== String(this.saved[f.field]));
    }

    cancel() {
        this.clearFiles();
        this.values = Object.assign({}, this.saved);
        this.errors = {};
        this.message = this.error = '';
    }

    private validate() {
        const e: { [field: string]: string } = {};
        this.inputs().forEach(f => {
            const v = this.values[f.field];
            const blank = v === null || v === undefined || String(v).trim() === '';
            const min = this.minOf(f);
            if (blank) {
                if (f.required) {
                    e[f.field] = f.type === 'checkbox' ? 'This must be ticked.'
                        : f.type === 'radio' || f.type === 'select' ? 'Choose an option.' : f.label + ' is required.';
                }
            } else if (f.type === 'int' && !(Number.isInteger(Number(v)) && Number(v) >= min)) {
                e[f.field] = 'Enter a whole number, ' + min + ' or more.';
            } else if (f.type === 'decimal' && !(isFinite(Number(v)) && Number(v) >= min)) {
                e[f.field] = 'Enter a number, ' + min + ' or more.';
            } else if (f.type === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v).trim())) {
                e[f.field] = 'Enter a valid email address.';
            }
        });
        this.card.fields.filter(f => f.type === 'image' && f.required).forEach(f => {
            if (!this.urls[f.field] && !this.files[f.field]) { e[f.field] = 'Choose a file to upload.'; }
        });
        this.errors = e;
        return !Object.keys(e).length;
    }

    save() {
        this.message = this.error = '';
        if (!this.validate()) {
            this.error = 'Check the highlighted fields.';
            return;
        }
        this.saving = true;
        const url = environment.api + 'system-settings/' + this.card.group;
        let request;
        if (Object.keys(this.files).length) {
            // PHP reads files only from a POST body.
            const body = new FormData();
            this.inputs().forEach(f => body.append(f.field, this.values[f.field] === null ? '' : String(this.values[f.field])));
            Object.keys(this.files).forEach(k => body.append(k, this.files[k]));
            request = this.http.post<any>(url, body);
        } else {
            request = this.http.put<any>(url, this.values);
        }
        request.subscribe(
            () => {
                this.saving = false;
                this.saved = Object.assign({}, this.values);
                this.message = 'Saved';
                if (Object.keys(this.files).length) {
                    this.clearFiles();
                    this.load();  // show the new file
                }
            },
            err => {
                this.saving = false;
                this.error = typeof err === 'string' ? err : 'The settings could not be saved.';
            }
        );
    }
}
